import {
  useEffect,
  useRef,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import confirmStyles from './ConfirmDialog.module.css';
import { Button } from './Button';
import { useDialogDismiss, type DialogDismiss } from './dialogDismiss';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(panel: HTMLElement): HTMLElement[] {
  const all = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
  const visible = all.filter((el) => el.getClientRects().length > 0);
  // jsdom has no layout, so fall back to every candidate there.
  return visible.length ? visible : all;
}

interface Props {
  dismiss: DialogDismiss;
  /** Accessible name; use labelledBy instead when a visible title has an id. */
  label?: string;
  labelledBy?: string;
  scrimClassName: string;
  panelClassName: string;
  panelStyle?: CSSProperties;
  children: ReactNode;
}

/**
 * One dismissal model for every console dialog: backdrop, Escape, unsaved-changes
 * guard, in-flight lock, focus trap and focus return. Keeps the caller's own CSS.
 * Portalled to <body> so a dialog opened from a drawer stacks by the --z-dialog
 * token, not by where it sits in the DOM.
 */
export function DialogShell({
  dismiss,
  label,
  labelledBy,
  scrimClassName,
  panelClassName,
  panelStyle,
  children,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const { kind, requestClose, confirming, setConfirming, onClose } = dismiss;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) {
      (panel.querySelector<HTMLElement>('[data-autofocus]') ?? panel).focus();
    }
    return () => {
      if (opener && opener !== document.body && opener.isConnected) opener.focus();
    };
  }, []);

  const onScrimClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (kind === 'confirm') requestClose();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      // Nested popovers (DatePicker, SearchableSelect, address list) consume their own Escape.
      if (e.defaultPrevented) return;
      e.preventDefault();
      e.stopPropagation();
      requestClose();
      return;
    }
    const panel = panelRef.current;
    if (e.key !== 'Tab' || !panel || !panel.contains(e.target as Node)) return;
    const items = focusables(panel);
    if (!items.length) {
      e.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault();
      e.stopPropagation();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      e.stopPropagation();
      first.focus();
    }
  };

  return createPortal(
    <div className={scrimClassName} onClick={onScrimClick} onKeyDown={onKeyDown}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={labelledBy ? undefined : label}
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={panelClassName}
        style={panelStyle}
      >
        {children}
      </div>
      {confirming && (
        <DiscardPrompt
          onKeep={() => setConfirming(false)}
          onDiscard={() => {
            // Reset first: a dialog that stays mounted while hidden must not reopen on the prompt.
            setConfirming(false);
            onClose();
          }}
        />
      )}
    </div>,
    document.body,
  );
}

function DiscardPrompt({ onKeep, onDiscard }: { onKeep: () => void; onDiscard: () => void }) {
  const { t } = useTranslation();
  const dismiss = useDialogDismiss({ kind: 'confirm', onClose: onKeep });
  return (
    <DialogShell
      dismiss={dismiss}
      labelledBy="discard-changes-title"
      scrimClassName={confirmStyles.backdrop}
      panelClassName={confirmStyles.modal}
    >
      <div className={confirmStyles.header}>
        <h3 id="discard-changes-title" className={confirmStyles.title}>
          {t('ui.dialogShell.discardTitle')}
        </h3>
      </div>
      <div className={confirmStyles.body}>
        <div className={confirmStyles.description}>{t('ui.dialogShell.discardDescription')}</div>
        <div className={confirmStyles.actions}>
          <Button variant="secondary" data-autofocus="" onClick={onKeep}>
            {t('ui.dialogShell.keepEditing')}
          </Button>
          <Button variant="danger" onClick={onDiscard}>
            {t('ui.dialogShell.discard')}
          </Button>
        </div>
      </div>
    </DialogShell>
  );
}
